import { useState, useCallback, useEffect } from 'react'
import {
  EXPERIMENT_KEY,
  SCROLL_BEHAVIOR_CONFIG,
} from '@/views/PreSales/PreSalesGiveawayView/components/Tabs/GiveawayTabs/constants'
import type {
  UseGiveawayTabsReturn,
  TabData,
} from '@/views/PreSales/PreSalesGiveawayView/components/Tabs/GiveawayTabs/types'
import { GiveawayTabEnum } from '@/views/PreSales/PreSalesGiveawayView/config'
import { useGiveawayContext } from '@/views/PreSales/PreSalesGiveawayView/context/GiveawayContext'
import { useIntersectionObserver } from './useIntersectionObserver'
import { useTabMappings } from './useTabMappings'

export const useGiveawayTabs = (): UseGiveawayTabsReturn => {
  const {
    selectedIndex,
    setSelectedIndex,
    isActive,
    tickets,
    entryVariantId,
    giveawayData: { tabs },
    tabsContainerRef,
    desktopTicketContainerRef,
    experiments,
    scrollToDetails,
    scrollToFaq,
    scrollToPrizes,
    detailsContainerRef,
    faqContainerRef,
    prizesContainerRef,
    theatricalSlug,
  } = useGiveawayContext()

  const [activeTab, setActiveTab] = useState<GiveawayTabEnum | null>(null)
  const isSingleTab = !!experiments?.[EXPERIMENT_KEY]

  const { refs, scrolls } = useTabMappings(
    { detailsContainerRef, faqContainerRef, prizesContainerRef },
    { scrollToDetails, scrollToFaq, scrollToPrizes },
  )

  const handleTabChange = useCallback(
    (index: number) => {
      const tab = tabs[index] as TabData
      if (isSingleTab) {
        scrolls[tab.type]?.()
        return
      }
      setSelectedIndex(index)
      tabsContainerRef.current?.scrollIntoView(SCROLL_BEHAVIOR_CONFIG)
    },
    [isSingleTab, tabs, scrolls, setSelectedIndex, tabsContainerRef],
  )

  const handleTabClick = useCallback(
    (tab: TabData) => {
      scrolls[tab.type]?.()
    },
    [scrolls],
  )

  useIntersectionObserver(isSingleTab, refs, tabs, setActiveTab)

  useEffect(() => {
    if (isSingleTab && tabs[0] && !activeTab) {
      setActiveTab(tabs[0].type)
    }
  }, [isSingleTab, tabs, activeTab])

  const shouldShowTicketSection = !!(isActive && tickets && entryVariantId)

  return {
    tabs: tabs as TabData[],
    isSingleTab,
    activeTab,
    selectedIndex,
    handleTabChange,
    handleTabClick,
    isActive,
    tabsContainerRef,
    desktopTicketContainerRef,
    shouldShowTicketSection,
    refs,
    tickets,
    entryVariantId,
    theatricalSlug,
  }
}
